import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    const language = localStorage.getItem('language') || 'en';
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    config.headers['Accept-Language'] = language;
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    const contentType = response.headers?.['content-type'] || '';
    if (response.config?.url?.startsWith('/api/') && !contentType.includes('application/json')) {
      return Promise.reject(new Error('Invalid API response content type'));
    }
    return response;
  },
  (error) => {
    // Only redirect to login for 401 errors on protected routes, not on login/register
    const isAuthEndpoint = error.config?.url?.includes('/api/auth/login') || 
                          error.config?.url?.includes('/api/auth/register');
    
    if (error.response?.status === 401 && !isAuthEndpoint) {
      // Token expired or invalid on a protected route
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (userData) => api.post('/api/auth/register', userData),
  login: (credentials) => api.post('/api/auth/login', new URLSearchParams(credentials), {
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
  }),
  getProfile: () => api.get('/api/auth/me'),
};

// Products API
export const productsAPI = {
  getAll: (params) => api.get('/api/products/', { params }),
  getById: (id) => api.get(`/api/products/${id}`),
  getBySlug: (slug) => api.get(`/api/products/slug/${slug}`),
  create: (productData) => api.post('/api/products/', productData),
  update: (id, productData) => api.put(`/api/products/${id}`, productData),
  delete: (id) => api.delete(`/api/products/${id}`),
  getCategories: () => api.get('/api/products/categories/list'),
  getManufacturers: () => api.get('/api/products/manufacturers/list'),
  getFitmentManufacturers: () => api.get('/api/products/fitment/manufacturers'),
  getFitmentModels: (manufacturer) => api.get(`/api/products/fitment/models/${encodeURIComponent(manufacturer)}`),
  getFitmentYears: (manufacturer, model) => api.get('/api/products/fitment/years', { params: { manufacturer, model } }),
  searchFitment: (params) => api.get('/api/products/fitment/search', { params }),
};

// Cart API
export const cartAPI = {
  get: () => api.get('/api/cart/'),
  addItem: (item) => api.post('/api/cart/items', item),
  updateItem: (itemId, data) => api.put(`/api/cart/items/${itemId}`, data),
  removeItem: (itemId) => api.delete(`/api/cart/items/${itemId}`),
  clear: () => api.delete('/api/cart/clear'),
};

// Orders API
export const ordersAPI = {
  create: (orderData) => api.post('/api/orders/', orderData),
  getAll: () => api.get('/api/orders/'),
  getById: (id) => api.get(`/api/orders/${id}`),
  updateStatus: (id, status) => api.put(`/api/orders/${id}/status`, { status }),
  getAllAdmin: (params) => api.get('/api/orders/admin/all', { params }),
};

// Users API
export const usersAPI = {
  getProfile: () => api.get('/api/users/me'),
  getAll: (params) => api.get('/api/users/', { params }),
  getById: (id) => api.get(`/api/users/${id}`),
};

// Contact API
export const contactAPI = {
  send: (formData) => api.post('/api/contact/send', formData),
  getAll: (params) => api.get('/api/contact/messages', { params }),
  updateStatus: (id, status) => api.patch(`/api/contact/messages/${id}/status`, { status }),
};

// Partnership API
export const partnershipAPI = {
  apply: (formData) => api.post('/api/partnership/apply', formData),
  getAll: (params) => api.get('/api/partnership/requests', { params }),
  updateStatus: (id, status) => api.patch(`/api/partnership/requests/${id}/status`, { status }),
};

// ECU Tuning API
export const ecuTuningAPI = {
  // ECU Tuning service requests
  submit: (formData) => api.post('/api/ecu-tuning/request', formData),
  getAll: (params) => api.get('/api/admin/ecu-tuning/requests', { params }),
  getById: (id) => api.get(`/api/admin/ecu-tuning/requests/${id}`),
  updateStatus: (id, status) => api.patch(`/api/admin/ecu-tuning/requests/${id}`, { status }),
  
  // ECU Tuning products (catalog)
  getAllProducts: (params) => api.get('/api/tuning/', { params }),
  getBySlug: (slug) => api.get(`/api/tuning/${slug}`),
  getFavourites: (params) => api.get('/api/tuning/favourites', { params }),
  getManufacturers: () => api.get('/api/tuning/manufacturers'),
  getModels: (manufacturer) => api.get('/api/tuning/models', { params: { manufacturer } }),
  getYears: (manufacturer, model) => api.get('/api/tuning/years', { params: { manufacturer, model } }),
};

export default api;
